/** @typedef {'user' | 'assistant'} Vertex360CopilotMessageRole */

/**
 * @typedef {Object} Vertex360CopilotMessage
 * @property {string} id
 * @property {Vertex360CopilotMessageRole} role
 * @property {string} content
 * @property {string} createdAt ISO timestamp
 */

export const VERTEX360_COPILOT_DRAWER_TRANSITION_MS = 320;

/** Drawer panel width — keep in sync with Vertex360CopilotDrawer aside max-w classes. */
export const VERTEX360_COPILOT_DRAWER_WIDTH_DEFAULT = 420;
export const VERTEX360_COPILOT_DRAWER_WIDTH_SM = 520;
export const VERTEX360_COPILOT_DRAWER_WIDTH_LG = 580;
export const VERTEX360_COPILOT_DRAWER_SM_BREAKPOINT_PX = 640;
export const VERTEX360_COPILOT_DRAWER_LG_BREAKPOINT_PX = 1024;

/** @returns {number} */
export const getVertex360CopilotDrawerWidth = () => {
  if (window.matchMedia(`(min-width: ${VERTEX360_COPILOT_DRAWER_LG_BREAKPOINT_PX}px)`).matches) {
    return VERTEX360_COPILOT_DRAWER_WIDTH_LG;
  }
  if (window.matchMedia(`(min-width: ${VERTEX360_COPILOT_DRAWER_SM_BREAKPOINT_PX}px)`).matches) {
    return VERTEX360_COPILOT_DRAWER_WIDTH_SM;
  }
  return VERTEX360_COPILOT_DRAWER_WIDTH_DEFAULT;
};

/**
 * Copilot drawer must sit above app shell (sidebar z-9999, header z-40,
 * timesheet popovers z-10020) when open. Rendered via portal on document.body.
 */
export const VERTEX360_COPILOT_DRAWER_Z_INDEX = 100000;

/**
 * @typedef {Object} Vertex360CopilotModuleTab
 * @property {string} id
 * @property {string} label
 */

/**
 * @typedef {Object} Vertex360CopilotModulePrompt
 * @property {string} id
 * @property {string} title
 * @property {string} prompt
 */

/**
 * Empty-state tabs — operational areas an SP manager uses to run the service.
 * Default tab is Participants.
 */
export const VERTEX360_COPILOT_MODULE_TABS = [
  { id: "participants", label: "Participants" },
  { id: "workforce", label: "Workforce" },
  { id: "rostering", label: "Rostering" },
  { id: "funding", label: "Funding" },
  { id: "safety", label: "Safety" },
];

/**
 * Empty-state prompts per tab.
 *
 * Rules:
 * - Questions a manager would ask to run the service (attention, risk, gaps)
 * - No how-to / navigation asks
 * - No directory dumps ("list active participants", "how many workers")
 * - No duplicate chips across tabs; keep titles short; prompts sendable as-is
 */
/** @type {Record<string, Vertex360CopilotModulePrompt[]>} */
export const VERTEX360_COPILOT_MODULE_PROMPTS = {
  participants: [
    {
      id: "plans-ending-8-weeks",
      title: "Plans ending in 8 weeks",
      prompt: "Which participants have NDIS plans ending in the next 8 weeks?",
    },
    {
      id: "casenotes-this-week",
      title: "Case notes this week",
      prompt: "List support coordination case notes from this week.",
    },
    {
      id: "participant-profile",
      title: "Participant profile",
      prompt: "Show the profile for a participant.",
    },
    {
      id: "inactive-participants",
      title: "Inactive participants",
      prompt: "List inactive participants.",
    },
    {
      id: "sil-residents",
      title: "SIL residents",
      prompt: "Which participants live in SIL homes?",
    },
  ],
  workforce: [
    {
      id: "workers-missing-docs",
      title: "Workers missing documents",
      prompt: "Which workers are missing required documents?",
    },
    {
      id: "docs-expiring-30d",
      title: "Documents expiring in 30 days",
      prompt: "Whose mandatory documents expire in the next 30 days?",
    },
    {
      id: "timesheets-awaiting-approval",
      title: "Timesheets to approve",
      prompt: "Show timesheets awaiting approval.",
    },
    {
      id: "worker-fatigue",
      title: "Overnight shifts last week",
      prompt: "Which workers had overnight shifts last week?",
    },
    {
      id: "worker-overload",
      title: "Hours last week",
      prompt: "How many shifts and hours did each worker work last week?",
    },
  ],
  rostering: [
    {
      id: "today-unassigned-shifts",
      title: "Today’s unassigned shifts",
      prompt: "Which of today’s shifts are still waiting for a worker?",
    },
    {
      id: "open-shifts-week",
      title: "Open shifts this week",
      prompt: "Which open shifts this week are still unassigned?",
    },
    {
      id: "best-match-worker",
      title: "Allocated workers",
      prompt: "Which workers are allocated to a participant?",
    },
    {
      id: "workers-match-requirements",
      title: "Workers rostered this week",
      prompt: "Which workers are rostered this week?",
    },
    {
      id: "participant-shifts-week",
      title: "Participant shifts this week",
      prompt: "What shifts are booked for participants this week?",
    },
    {
      id: "worker-roster-week",
      title: "A worker’s roster this week",
      prompt: "What shifts does a worker have this week?",
    },
  ],
  funding: [
    {
      id: "plans-ending-soon",
      title: "Plans ending in 30 days",
      prompt: "Which NDIS plans are ending in the next 30 days?",
    },
    {
      id: "active-funding-plans",
      title: "Active funding plans",
      prompt: "Show active funding plans.",
    },
    {
      id: "outstanding-cos-ah-claims",
      title: "Outstanding COS or AH claims",
      prompt: "Which COS or AH claims are still not submitted?",
    },
    {
      id: "shifts-not-invoiced",
      title: "Last week’s timesheets not invoiced",
      prompt: "Which timesheets last week have not been invoiced yet?",
    },
    {
      id: "unpaid-invoices-month",
      title: "Invoices this month",
      prompt: "Which roster invoices were created this month?",
    },
  ],
  safety: [
    {
      id: "incidents-open-overdue",
      title: "Open incidents",
      prompt: "Which incidents are still open?",
    },
    {
      id: "high-severity-month",
      title: "Incidents this month",
      prompt: "Which incidents were logged this month?",
    },
    {
      id: "reportable-not-submitted",
      title: "Not reported to the commission",
      prompt:
        "Which incidents have not been reported to the NDIS commission?",
    },
    {
      id: "open-complaints",
      title: "Open complaints",
      prompt: "Which complaints are still open?",
    },
    {
      id: "restricted-shift-notes",
      title: "Shift notes this week",
      prompt: "List this week’s timesheet shift notes.",
    },
  ],
};

export const VERTEX360_COPILOT_DEFAULT_MODULE_TAB_ID = "participants";

/** Fallback chips when an older message has no server suggestions. */
export const VERTEX360_COPILOT_FOLLOW_UP_CHIPS = [
  "Which participants have NDIS plans ending in the next 8 weeks?",
  "Which of today’s shifts are still waiting for a worker?",
  "Which incidents are still open?",
];

export const VERTEX360_COPILOT_WELCOME = {
  title: "How can Vertex360 Copilot help?",
  subtitle:
    "Ask how the service is running — who needs attention, workforce risk, this week’s roster, funding, and safety. Pick a tab or type a question.",
};

/** Accessible label while waiting for or receiving an assistant reply. */
export const VERTEX360_COPILOT_RESPONDING_LABEL = "Vertex360 Copilot is responding";

/**
 * Rotating status lines while waiting for the first stream chunk.
 * Phrases stay honest (no fake “query finished” claims) and keep wait feel active.
 */
export const VERTEX360_COPILOT_THINKING_STEPS = [
  "Reading your question",
  "Looking across your organisation",
  "Gathering the right details",
  "Checking related records",
  "Shaping a clear answer",
  "Almost ready",
];

export const VERTEX360_COPILOT_THINKING_STEP_MS = 2200;

/** Typewriter reveal cadence — tuned to feel readable instead of instant. */
export const VERTEX360_COPILOT_TYPEWRITER_INTERVAL_MS = 22;
export const VERTEX360_COPILOT_TYPEWRITER_CHARS_PER_TICK = 1;
